import { type Component, markRaw } from "vue";

import { BlockLayout } from "@/enums/bpCustomisation";
import {
  type Block,
  BlockType,
  type BlockWithIcon,
  DimensionsMode,
} from "@/models/borrowerPlatformTemplates";

import ButtonBlock from "@/components/borrowerPlatform/Blocks/ButtonBlock.vue";
import IllustrationBlock from "@/components/borrowerPlatform/Blocks/IllustrationBlock.vue";
import ImageBlock from "@/components/borrowerPlatform/Blocks/ImageBlock.vue";
import TextBlock from "@/components/borrowerPlatform/Blocks/TextBlock.vue";
import TilesBlock from "@/components/borrowerPlatform/Blocks/TilesBlock.vue";
import IconBank from "@/components/icons/borrowerPlatform/borrowerTabs/IconBank.vue";
import IconBriefcase from "@/components/icons/borrowerPlatform/borrowerTabs/IconBriefcase.vue";
import IconBusiness from "@/components/icons/borrowerPlatform/borrowerTabs/IconBusiness.vue";
import IconButton from "@/components/icons/borrowerPlatform/borrowerTabs/IconButton.vue";
import IconChat from "@/components/icons/borrowerPlatform/borrowerTabs/IconChat.vue";
import IconCheckboard from "@/components/icons/borrowerPlatform/borrowerTabs/IconCheckboard.vue";
import IconCreditCard from "@/components/icons/borrowerPlatform/borrowerTabs/IconCreditCard.vue";
import IconDocument from "@/components/icons/borrowerPlatform/borrowerTabs/IconDocument.vue";
import IconDollar from "@/components/icons/borrowerPlatform/borrowerTabs/IconDollar.vue";
import IconGroup from "@/components/icons/borrowerPlatform/borrowerTabs/IconGroup.vue";
import IconHandshake from "@/components/icons/borrowerPlatform/borrowerTabs/IconHandshake.vue";
import IconHouse from "@/components/icons/borrowerPlatform/borrowerTabs/IconHouse.vue";
import IconIllustration from "@/components/icons/borrowerPlatform/borrowerTabs/IconIllustration.vue";
import IconImg from "@/components/icons/borrowerPlatform/borrowerTabs/IconImg.vue";
import IconLayout from "@/components/icons/borrowerPlatform/borrowerTabs/IconLayout.vue";
import IconLeter from "@/components/icons/borrowerPlatform/borrowerTabs/IconLeter.vue";
import IconLogout from "@/components/icons/borrowerPlatform/borrowerTabs/IconLogout.vue";
import IconPiechart from "@/components/icons/borrowerPlatform/borrowerTabs/IconPiechart.vue";
import IconQuestionmark from "@/components/icons/borrowerPlatform/borrowerTabs/IconQuestionmark.vue";
import IconText from "@/components/icons/borrowerPlatform/borrowerTabs/IconText.vue";
import IconTiles from "@/components/icons/borrowerPlatform/borrowerTabs/IconTiles.vue";
import IconUser from "@/components/icons/borrowerPlatform/borrowerTabs/IconUser.vue";
import IconWallet from "@/components/icons/borrowerPlatform/borrowerTabs/IconWallet.vue";
import IconWeight from "@/components/icons/borrowerPlatform/borrowerTabs/IconWeight.vue";

export const BORROWER_PLATFORM_BLOCK_COMPONENTS: Record<BlockType, Component> =
  {
    [BlockType.ILLUSTRATION]: markRaw(IllustrationBlock),
    [BlockType.TEXT]: markRaw(TextBlock),
    [BlockType.IMAGE]: markRaw(ImageBlock),
    [BlockType.BUTTON]: markRaw(ButtonBlock),
    [BlockType.TILES]: markRaw(TilesBlock),
  };

export const BLOCK_TYPES: BlockWithIcon[] = [
  {
    type: BlockType.IMAGE,
    icon: "image",
    iconComponent: IconImg,
    labelKey: "BORROWER_PLATFORM.ADD_BLOCK_MENU.IMAGE",
  },
  {
    type: BlockType.ILLUSTRATION,
    icon: "photograph",
    iconComponent: IconIllustration,
    labelKey: "BORROWER_PLATFORM.ADD_BLOCK_MENU.ILLUSTRATION",
  },
  {
    type: BlockType.TEXT,
    icon: "text-box",
    iconComponent: IconText,
    labelKey: "BORROWER_PLATFORM.ADD_BLOCK_MENU.TEXT",
  },
  {
    type: BlockType.BUTTON,
    icon: "cursor-click",
    iconComponent: IconButton,
    labelKey: "BORROWER_PLATFORM.ADD_BLOCK_MENU.BUTTON",
  },
  {
    type: BlockType.TILES,
    icon: "template",
    iconComponent: IconTiles,
    labelKey: "BORROWER_PLATFORM.ADD_BLOCK_MENU.TILES",
  },
];

export const TAB_ICONS: Record<string, Component> = {
  bank: markRaw(IconBank),
  briefcase: markRaw(IconBriefcase),
  business_loans: markRaw(IconBusiness),
  chat: markRaw(IconChat),
  checkboard: markRaw(IconCheckboard),
  credit_card: markRaw(IconCreditCard),
  document: markRaw(IconDocument),
  dollar: markRaw(IconDollar),
  group: markRaw(IconGroup),
  handshake: markRaw(IconHandshake),
  house: markRaw(IconHouse),
  layout: markRaw(IconLayout),
  letter: markRaw(IconLeter),
  logout: markRaw(IconLogout),
  piechart: markRaw(IconPiechart),
  questionmark: markRaw(IconQuestionmark),
  wallet: markRaw(IconWallet),
  weight: markRaw(IconWeight),
  user: markRaw(IconUser),
};

export const BLOCK_FACTORIES: Record<
  BlockType,
  (baseBlock: Partial<Block>) => Block
> = {
  [BlockType.TEXT]: (baseBlock) =>
    ({
      ...baseBlock,
      type: BlockType.TEXT,
      title: "",
      description: "",
    }) as Block,

  [BlockType.BUTTON]: (baseBlock) =>
    ({
      ...baseBlock,
      type: BlockType.BUTTON,
      text: "",
      url: "",
    }) as Block,

  [BlockType.IMAGE]: (baseBlock) =>
    ({
      ...baseBlock,
      type: BlockType.IMAGE,
      file_id: null,
      dimensions: {
        mode: DimensionsMode.SCALE,
        scale: 100,
      },
    }) as Block,

  [BlockType.ILLUSTRATION]: (baseBlock) =>
    ({
      ...baseBlock,
      type: BlockType.ILLUSTRATION,
      icon: "",
    }) as Block,

  [BlockType.TILES]: (baseBlock) =>
    ({
      ...baseBlock,
      type: BlockType.TILES,
      tiles: [],
      layout: BlockLayout.ONE_COLUMN,
    }) as Block,
};
